import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Badge, Button, Card } from '@wit-support/ui';
import { Languages } from 'lucide-react';
import { Time } from './time.js';
import { AttachmentLink } from './attachment-link.js';
import { cn } from './lib/cn.js';
import { stripInlineAttachmentMarkers as stripMarkers } from './lib/strip-inline-attachment-markers.js';
const COLLAPSE_THRESHOLD = 1200;
const BLANK_LINE_COLLAPSE_RE = /\n\s*\n\s*\n+/g;
/**
 * Strips inline-attachment markers (shared with conversation-list-item's
 * snippet cleanup — see lib/strip-inline-attachment-markers.ts) and collapses
 * the 3+ blank lines that appear once the markers are gone. The stored
 * body_text stays untouched; this only affects render.
 */
function stripInlineAttachmentMarkers(text) {
    return stripMarkers(text).replace(BLANK_LINE_COLLAPSE_RE, '\n\n').trim();
}
/**
 * Plain-text quote boundary lines. The reply body is split at the FIRST match:
 *   - `On <…> wrote:` (English Gmail/Apple Mail attribution)
 *   - RU `<date/time …>, … писал(а):` / `… написал(а):`
 *   - Arabic `<date/time …> … كتب:`
 *   - a run of lines each starting with `>` (classic quote prefix)
 *
 * To avoid false-positives on real sentences that merely END in a verb like
 * «Я вам уже написал:» or «… wrote:», the RU/Arabic shapes REQUIRE date/time
 * context on the same line (a digit — every real mail-client attribution
 * embeds the original message's date/time). The English `On … wrote:` shape is
 * already distinctive enough on its own. A bare `>`-quoted run still counts.
 */
const PLAINTEXT_ATTRIBUTION_RE = /^\s*(?:On\s.+\swrote:|(?=.*[\d٠-٩۰-۹]).*(?:на)?писал(?:а|\(а\))?\s*:|(?=.*[\d٠-٩۰-۹]).*كتب\s*:)\s*$/im;
const PLAINTEXT_QUOTE_PREFIX_RE = /^\s*>/;
/**
 * Split a customer reply into `{ visible, quoted }`. Pure — no DOM mutation of
 * any live node (HTML parsing happens in a throwaway document). `quoted` is
 * `null` when no quote boundary is found.
 *
 * HTML: parse with DOMParser, find the first quote container
 * (`div.gmail_quote`, `blockquote`/`blockquote[type=cite]`, Outlook
 * `#divRplyFwdMsg` / `#appendonsend`), snapshot + remove it, return the
 * remaining body HTML as `visible` and the removed subtree's HTML as `quoted`.
 *
 * Plaintext: split at the first attribution line or `>`-prefixed run.
 */
export function splitQuotedReply(body, isHtml) {
    if (isHtml) {
        if (typeof DOMParser === 'undefined')
            return { visible: body, quoted: null };
        const doc = new DOMParser().parseFromString(body, 'text/html');
        const quoteNode = doc.querySelector('div.gmail_quote') ??
            doc.querySelector('blockquote[type="cite"]') ??
            doc.querySelector('blockquote') ??
            doc.querySelector('#divRplyFwdMsg') ??
            doc.querySelector('#appendonsend');
        if (!quoteNode)
            return { visible: body, quoted: null };
        const quoted = quoteNode.outerHTML;
        quoteNode.parentNode?.removeChild(quoteNode);
        const remainingText = doc.body?.textContent?.replace(/ /g, ' ').trim() ?? '';
        // A fresh reply can be media-only (e.g. an inline image with no words) — empty
        // textContent but real visible content outside the quote. Treat such media as
        // non-empty so we don't mis-classify it as a pure forward and drop the toggle.
        const hasMedia = doc.body?.querySelector('img,table,video,audio,iframe,object,embed') != null;
        if (remainingText === '' && !hasMedia) {
            // Pure forward / quote-only message: the customer added no fresh text (and no
            // media) above the quoted/forwarded content. Removing the quote would leave a
            // BLANK bubble that hides everything behind «Показать цитату». Show the full
            // original body instead.
            return { visible: body, quoted: null };
        }
        const visible = doc.body ? doc.body.innerHTML : body;
        return { visible: visible.trim(), quoted };
    }
    const lines = body.split('\n');
    let boundary = -1;
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (PLAINTEXT_QUOTE_PREFIX_RE.test(line) || PLAINTEXT_ATTRIBUTION_RE.test(line)) {
            boundary = i;
            break;
        }
    }
    if (boundary === -1)
        return { visible: body, quoted: null };
    const visible = lines.slice(0, boundary).join('\n').trimEnd();
    if (visible.trim() === '') {
        // Nothing freshly written before the quote (quote-only / pure forward) — show
        // the whole body rather than a blank bubble.
        return { visible: body, quoted: null };
    }
    const quoted = lines.slice(boundary).join('\n').trim();
    return { visible, quoted: quoted.length > 0 ? quoted : null };
}
/**
 * Renders a plaintext message body with the SAME quote-collapse + length-collapse
 * behavior the inbound bubble applies: the freshly-written part is shown, long
 * text past `COLLAPSE_THRESHOLD` is truncated behind a «Показать ещё» toggle, and
 * the quoted prior-email history is hidden behind a «Показать цитату» ghost toggle.
 *
 * Used both by `MessageBubble` (its plaintext branch) and by the translation
 * renderer so a translated message collapses identically to the original.
 */
export function MessageBody({ text, splitQuote = true, }) {
    const [expanded, setExpanded] = React.useState(false);
    const [quoteShown, setQuoteShown] = React.useState(false);
    // `text` is expected to be already cleaned of inline-attachment markers by
    // the caller (the bubble strips them only when attachments exist; translated
    // text never carries them), so we do not re-strip here.
    const cleaned = text;
    const hasText = cleaned.length > 0;
    // Split the FULL cleaned text first so a quote boundary past the
    // COLLAPSE_THRESHOLD char isn't missed; THEN collapse the resulting visible
    // part. (Splitting the pre-truncated text would drop any boundary beyond
    // char COLLAPSE_THRESHOLD and the toggle would never appear.)
    const split = React.useMemo(() => (hasText && splitQuote ? splitQuotedReply(cleaned, false) : null), [hasText, splitQuote, cleaned]);
    const plainSource = split ? split.visible : cleaned;
    const quotedPlain = split ? split.quoted : null;
    const shouldCollapse = hasText && plainSource.length > COLLAPSE_THRESHOLD;
    const visiblePlain = shouldCollapse && !expanded ? plainSource.slice(0, COLLAPSE_THRESHOLD) : plainSource;
    const hasQuote = quotedPlain !== null;
    if (!hasText)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "whitespace-pre-wrap text-sm", children: visiblePlain }), shouldCollapse ? (_jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "mt-2", onClick: () => setExpanded((v) => !v), children: expanded ? 'Свернуть' : 'Показать ещё' })) : null, hasQuote ? (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "mt-2", "aria-expanded": quoteShown, onClick: () => setQuoteShown((v) => !v), children: quoteShown ? 'Скрыть цитату' : 'Показать цитату' }), quoteShown ? (_jsx("div", { className: "mt-2 border-l-2 border-[var(--color-border-subtle)] pl-3", children: _jsx("p", { className: "whitespace-pre-wrap text-sm text-[var(--color-text-muted)]", children: quotedPlain }) })) : null] })) : null] }));
}
const SENDER_LABELS = {
    contact: 'Inbound',
    agent: 'Outbound',
    system: 'System',
    bot: 'Bot',
};
const SENDER_VARIANT = {
    contact: 'neutral',
    agent: 'accent',
    system: 'info',
    bot: 'info',
};
export const MessageBubble = React.forwardRef(({ message, className, translation = null, showTranslated = false, onToggleTranslated, onTranslate, translating = false, canTranslate = false, }, ref) => {
    const isOutbound = message.direction === 'out';
    const label = SENDER_LABELS[message.senderKind] ?? 'Message';
    const variant = SENDER_VARIANT[message.senderKind] ?? 'neutral';
    const hasAttachments = !!message.attachments && message.attachments.length > 0;
    // Raw body_text from Gmail often contains `[image: filename]` placeholders
    // that duplicate attachments we already render inline below. Strip them
    // (only when attachments exist — zero risk of eating real text) so the
    // bubble doesn't show both the image and its text stand-in.
    const cleanedText = typeof message.bodyText === 'string'
        ? hasAttachments
            ? stripInlineAttachmentMarkers(message.bodyText)
            : message.bodyText
        : '';
    const hasText = cleanedText.length > 0;
    // Body source priority:
    //   1. bodyHtml (full email document) → sandboxed iframe. Outbound replies
    //      store the branded HTML email here; their bodyText is a lossy plain
    //      flattening of that document (header/footer/links mashed together),
    //      so rendering the HTML is the only faithful view.
    //   2. bodyText  → plain-text render (clean inbound customer text).
    //   3. neither   → nothing.
    const html = typeof message.bodyHtml === 'string' && message.bodyHtml.length > 0 ? message.bodyHtml : null;
    const hasHtml = html !== null;
    // Inbound customer replies often append the quoted prior-email history,
    // which is redundant here since the whole thread is already visible. Split
    // it out (inbound only) and hide it behind a ghost toggle. Outbound is
    // never split — we always render the full body the agent sent.
    const [quoteShown, setQuoteShown] = React.useState(false);
    const htmlSplit = React.useMemo(() => (!isOutbound && hasHtml ? splitQuotedReply(html, true) : null), [isOutbound, hasHtml, html]);
    const visibleHtml = htmlSplit ? htmlSplit.visible : html;
    const quotedHtml = htmlSplit ? htmlSplit.quoted : null;
    const hasHtmlQuote = quotedHtml !== null;
    const outerAlign = isOutbound ? 'ml-auto' : 'mr-auto';
    // Translation affordance — inbound only.
    // showsTranslation=true  → show translated text + «Оригинал» toggle
    // translation present, not showing → show original text + «Перевод» toggle
    // no translation, canTranslate → show «Перевести» button
    const showTranslationControls = !isOutbound && (!!translation || canTranslate);
    const activelyShowingTranslation = showTranslationControls && !!translation && showTranslated;
    return (_jsx("article", { ref: ref, className: cn('w-full max-w-[680px]', outerAlign, className), "aria-label": label, children: _jsxs(Card, { className: cn('overflow-hidden', isOutbound &&
                'border-transparent bg-[var(--color-brand-bg)] text-[var(--color-brand-fg)]'), children: [_jsxs("div", { className: "flex items-center gap-2 px-4 pt-3", children: [_jsx(Badge, { variant: variant, size: "sm", children: label }), _jsx(Time, { iso: message.createdAt, preset: "short", className: cn('text-xs', isOutbound
                                ? 'text-[var(--color-brand-fg)] opacity-80'
                                : 'text-[var(--color-text-muted)]') }), message.subject ? (_jsx("span", { className: cn('ml-auto truncate text-xs', isOutbound
                                ? 'text-[var(--color-brand-fg)] opacity-80'
                                : 'text-[var(--color-text-muted)]'), title: message.subject, children: message.subject })) : null] }), _jsx("div", { className: "px-4 py-3", children: activelyShowingTranslation ? (
                    // Show translation as plain text (never HTML). Do NOT quote-split a
                    // model-generated translation — heuristic splitting of a translated
                    // string is more likely wrong than helpful. Strip inline-attachment
                    // markers when the message HAS attachments — same as the original
                    // body branch — so a translation that echoed an `[image: …]` /
                    // `[изображение: …]` placeholder doesn't show it literally while the
                    // original view hides it (consistent rendering across the toggle).
                    //
                    // ACCEPTED TRADEOFF: toggling «Перевод» ↔ «Оригинал» unmounts the
                    // active subtree and mounts the other, so each branch owns its own
                    // `expanded`/`quoteShown` useState — flipping the toggle resets those
                    // states to their defaults. This is intentional: translations are
                    // usually short and don't need quote-splitting (splitQuote=false above),
                    // so there is nothing to lose on the translation side. Preserving
                    // per-view expand/quote state would require either lifting that state up
                    // to the bubble or keeping both subtrees mounted via CSS visibility —
                    // both add complexity for minimal user benefit.
                    _jsx(MessageBody, { text: hasAttachments
                            ? stripInlineAttachmentMarkers(translation.text)
                            : translation.text, splitQuote: false })) : hasHtml ? (_jsxs(_Fragment, { children: [_jsx(HtmlBody, { html: visibleHtml, title: `${label} message body` }), hasHtmlQuote ? (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "mt-2", "aria-expanded": quoteShown, onClick: () => setQuoteShown((v) => !v), children: quoteShown ? 'Скрыть цитату' : 'Показать цитату' }), quoteShown ? (_jsx("div", { className: "mt-2 border-l-2 border-[var(--color-border-subtle)] pl-3", children: _jsx(HtmlBody, { html: quotedHtml, title: `${label} quoted history` }) })) : null] })) : null] })) : hasText ? (_jsx(MessageBody, { text: cleanedText, splitQuote: !isOutbound })) : null }), showTranslationControls ? (_jsxs("div", { className: "flex items-center gap-1.5 border-t border-[var(--color-border-subtle)] px-4 py-2", children: [_jsx(Languages, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), translation ? (
                        // Toggle between original and translated
                        _jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "h-5 px-2 text-xs", "aria-label": showTranslated ? 'Показать оригинал' : 'Показать перевод', onClick: () => onToggleTranslated?.(!showTranslated), children: showTranslated ? 'Оригинал' : 'Перевод' })) : (
                        // No translation yet — offer to fetch one
                        _jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "h-5 gap-1 px-2 text-xs", disabled: translating, "aria-busy": translating, "aria-label": translating ? 'Переводим сообщение…' : 'Перевести сообщение', onClick: () => onTranslate?.(), children: translating ? 'Переводим…' : 'Перевести' }))] })) : null, hasAttachments ? _jsx(AttachmentsBlock, { attachments: message.attachments }) : null] }) }));
});
MessageBubble.displayName = 'MessageBubble';
const HTML_BODY_MIN_HEIGHT = 60;
/**
 * Renders a full HTML email document in a sandboxed iframe so its own styles
 * (the branded email layout brings its own <head>/<style>) are isolated from
 * the admin UI and cannot leak out — and, critically, so embedded scripts
 * cannot execute.
 *
 * Sandbox: `allow-same-origin` ONLY. We deliberately do NOT add
 * `allow-scripts` — that would re-enable script execution and reopen an XSS
 * hole via the email HTML. `allow-same-origin` (without `allow-scripts`) runs
 * no JS from the document yet still lets the parent read `contentDocument` to
 * measure the rendered height.
 *
 * Height: the iframe grows to its full measured `scrollHeight` (no ceiling)
 * and sets `overflow: hidden` so the PAGE/thread scrolls rather than an inner
 * scrollbar that would clip the email. A sane minimum keeps tiny bodies from
 * collapsing to nothing.
 *
 * Late reflow: content can grow AFTER the initial `onLoad` measure — web
 * `@font-face` swaps, `loading="lazy"` images decoding in. A one-shot measure
 * would clip that growth permanently (parent Card is `overflow-hidden`). So we
 * attach a `ResizeObserver` to the iframe document's body/root and re-measure
 * on every reflow, disconnecting on unmount. Guarded for SSR / null
 * contentDocument / environments without ResizeObserver.
 */
/**
 * Sandbox tokens for the email-body iframe.
 *
 * SECURITY INVARIANT: this MUST NOT include `allow-scripts`. `allow-same-origin`
 * alone lets the parent read `contentDocument` to measure height while keeping
 * all script execution in the (untrusted) email HTML disabled. Adding
 * `allow-scripts` ALONGSIDE `allow-same-origin` would let the document script
 * its own sandboxing away — reopening the stored-XSS hole. Locked by a unit
 * test asserting the rendered `sandbox` attr never contains `allow-scripts`.
 */
const HTML_BODY_SANDBOX = 'allow-same-origin';
const HtmlBody = ({ html, title }) => {
    const [height, setHeight] = React.useState(HTML_BODY_MIN_HEIGHT);
    const iframeRef = React.useRef(null);
    const observerRef = React.useRef(null);
    const measure = React.useCallback((iframe) => {
        // allow-same-origin (no allow-scripts) keeps contentDocument readable.
        const doc = iframe?.contentDocument;
        const root = doc?.documentElement;
        const body = doc?.body;
        const scrollHeight = Math.max(body?.scrollHeight ?? 0, root?.scrollHeight ?? 0);
        if (scrollHeight > 0) {
            // Grow to the true content height — no max ceiling, so the bubble never
            // clips its own content behind an internal scrollbar.
            setHeight(Math.max(scrollHeight, HTML_BODY_MIN_HEIGHT));
        }
    }, []);
    const handleLoad = React.useCallback((iframe) => {
        measure(iframe);
        // Re-measure on late reflow (font swaps, lazy images). Guard for SSR /
        // null contentDocument / no ResizeObserver support.
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (typeof ResizeObserver === 'undefined')
            return;
        const doc = iframe.contentDocument;
        if (!doc)
            return;
        const ro = new ResizeObserver(() => measure(iframe));
        if (doc.body)
            ro.observe(doc.body);
        if (doc.documentElement)
            ro.observe(doc.documentElement);
        observerRef.current = ro;
    }, [measure]);
    React.useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, []);
    return (_jsx("iframe", { ref: iframeRef, title: title, srcDoc: html, sandbox: HTML_BODY_SANDBOX, className: "w-full rounded border-0 bg-[var(--color-email-canvas)]", style: { height, overflow: 'hidden' }, scrolling: "no", onLoad: (e) => handleLoad(e.currentTarget) }));
};
/**
 * Every attachment renders as a uniform square tile — image, PDF, audio,
 * video, archive, unknown — all the same 140×140 footprint. They wrap in a
 * horizontal flex grid so a message with many files stays compact.
 */
const AttachmentsBlock = ({ attachments, }) => (_jsx("div", { className: "flex flex-wrap gap-2 border-t border-[var(--color-border-subtle)] px-4 py-3", children: attachments.map((att) => (_jsx(AttachmentLink, { attachment: {
            id: att.id,
            filename: att.filename,
            contentType: att.contentType,
            sizeBytes: att.sizeBytes,
        } }, att.id))) }));
//# sourceMappingURL=message-bubble.js.map